import type { MetricType } from 'store/settings/types';
import { CONVERSIONS, convertToCurrentMetric } from './conversions';
import { store } from 'createStore';

export const maxPsdSize = 2600;

/* Returns an error if size < 0 or size > maxSize */
export const validateAddSize = (
  sizeValue: number | undefined,
  newSizeList: number[],
) => {
  if (typeof sizeValue !== 'number') {
    return '';
  }
  const sizeExist = newSizeList.includes(sizeValue);

  if (sizeValue < 0) {
    return `Size >= ${0}`;
  }
  if (sizeExist) {
    return 'Size exist';
  }

  if (maxPsdSize && sizeValue > maxPsdSize) {
    const metric = store.getState().settingsState.metricType;
    const convertedMaxSize = convertToCurrentMetric({
      value: maxPsdSize,
      metricType: metric,
      conversionType: CONVERSIONS.MILLIMETER,
      decimals: 2,
    });
    return `Size > ${convertedMaxSize}`;
  }

  return '';
};

/* Returns an error if 1) percent < 0 or percent >= 100,
2) percent > percentAfter, 3) percent < percentAfter  */
export const validateAddPercent = (
  newPercentList: number[],
  newSizeList: number[],
  percentValue: number | undefined,
  sizeValue: number | undefined,
) => {
  if (typeof percentValue !== 'number' || typeof sizeValue !== 'number') {
    return '';
  }
  const addIndex = newSizeList.findIndex((s) => sizeValue < s);

  if (addIndex !== -1) {
    /* FIRST - NO VALUE BEFORE - COMPARE TO 0 & AFTER */
    if (addIndex === 0) {
      const percentBefore = 0;
      const percentAfter = newPercentList[0];
      if (percentValue < percentBefore || percentValue >= percentAfter) {
        return `Percent not >=${percentBefore} & <${percentAfter}`;
      }
    } else {
      /* MIDDLE - COMPARE TO BEFORE & AFTER  */
      const percentBefore = newPercentList[addIndex - 1];
      const percentAfter = newPercentList[addIndex];

      if (percentBefore >= percentValue || percentAfter <= percentValue) {
        return `Percent not ${
          percentBefore === -0.1 ? `>=0` : `>${percentBefore}`
        } & <${percentAfter}`;
      }
    }
  } else {
    /* LAST - NO VALUE AFTER - COMPARE TO BEFORE & 100  */
    const percentBefore = newPercentList[newPercentList.length - 1];
    const percentAfter = 100;
    if (percentValue <= percentBefore || percentValue > percentAfter) {
      return `Percent not >${percentBefore} & <=${percentAfter}`;
    }
  }
  return '';
};

/* Returns an error if 1) size < 0 or size >= 100,
2) size > sizeAfter, 3) size < sizeBefore  */
export const validateEditSize = (
  newSizeList: number[],
  editIndex: number,
  sizeValue: number | undefined,
  metricType: MetricType,
) => {
  if (typeof sizeValue !== 'number') {
    return '';
  }

  const sizeBefore = newSizeList[editIndex - 1];
  const sizeAfter = newSizeList[editIndex + 1];

  const convert = (val: number, decimals?: number) =>
    convertToCurrentMetric({
      value: val,
      metricType: metricType,
      conversionType: CONVERSIONS.MILLIMETER,
      decimals: decimals,
    });

  if (typeof sizeBefore === 'undefined') {
    /* FIRST - NO VALUE BEFORE - COMPARE TO 0 & AFTER */
    const convertedSizeAfter = convert(sizeAfter);

    if (
      typeof convertedSizeAfter !== 'undefined' &&
      (0 > convertedSizeAfter || convertedSizeAfter <= sizeValue)
    ) {
      return `Size not >=${0} & <${convertedSizeAfter}`;
    }
  } else if (typeof sizeAfter === 'undefined') {
    /* LAST - NO VALUE AFTER - COMPARE TO BEFORE ONLY */
    const convertedSizeBefore = convert(sizeBefore);
    const convertedMaxSize = convert(maxPsdSize, 3);

    if (
      typeof convertedSizeBefore !== 'undefined' &&
      typeof convertedMaxSize !== 'undefined' &&
      (convertedSizeBefore >= sizeValue || convertedMaxSize < sizeValue)
    ) {
      return `Size not >${convertedSizeBefore} & <${convertedMaxSize}`;
    }
  } else {
    const convertedSizeAfter = convert(sizeAfter);
    const convertedSizeBefore = convert(sizeBefore);

    if (
      typeof convertedSizeBefore !== 'undefined' &&
      typeof convertedSizeAfter !== 'undefined' &&
      (convertedSizeBefore >= sizeValue || convertedSizeAfter <= sizeValue)
    ) {
      /* MIDDLE - COMPARE TO BEFORE & AFTER  */
      return `Size not >${convertedSizeBefore} & <${convertedSizeAfter}`;
    }
  }

  return '';
};

/* Returns an error if 1) percent < 0 or percent >= 100,
2) percent > percentAfter, 3) percent < percentAfter  */
export const validateEditPercent = (
  newPercentList: number[],
  newSizeList: number[],
  percentValue: number | undefined,
  editIndex: number | undefined,
) => {
  if (typeof percentValue !== 'number' || typeof editIndex !== 'number') {
    return '';
  }

  if (editIndex !== -1 && editIndex !== newPercentList.length - 1) {
    const percentBefore = newPercentList[editIndex - 1];
    const percentAfter = newPercentList[editIndex + 1];

    if (typeof percentBefore === 'undefined') {
      /* FIRST - NO VALUE BEFORE - COMPARE TO 0 & AFTER */
      if (0 > percentValue || percentAfter <= percentValue) {
        return `Percent Not >=${0} & <${percentAfter}`;
      }
    } else if (percentBefore >= percentValue || percentAfter <= percentValue) {
      /* MIDDLE - COMPARE TO BEFORE & AFTER  */
      return `Percent not >${percentBefore} & <${percentAfter}`;
    }
  } else {
    /* LAST - NO VALUE AFTER - COMPARE TO BEFORE & 100  */
    const percentBefore = newPercentList[newSizeList.length - 2];
    const percentAfter = 100;
    if (percentBefore >= percentValue || percentAfter < percentValue) {
      return `Percent not >${percentBefore} & <=${percentAfter}`;
    }
  }

  return '';
};
